import { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { CancelOutlined as CancelOutlinedIcon } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useCallback, useState } from 'react';

import { selectTaskPropertiesStrict } from '../../../../features/workflow/selectors';
import {
  unbindConditionParameter,
  unbindParameterDefinitionOfTask,
} from '../../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { getParameterValueStr } from '../../../../utils';
import {
  getParameterIcon,
  getParameterType,
} from '../../../GraphEditorToolbars/ParametersToolbar/utils';

type ExposingParameterChipProps = {
  applyParamValue?: boolean;
  fieldName: string;
  parameter: ParameterDefinition | undefined;
  taskId: string;
};

export const ExposingParameterChip = ({
  applyParamValue = true,
  fieldName,
  parameter,
  taskId,
}: ExposingParameterChipProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const dispatch = useAppDispatch();
  const task = useAppSelector(selectTaskPropertiesStrict)[taskId];

  const handleUnbindParameter = useCallback(() => {
    if (!parameter) return;

    const paramUpdate = applyParamValue ? { parameter } : {};

    // Special case for condition tasks
    if (task?.propertiesType === 'condition') {
      dispatch(
        unbindConditionParameter({
          taskId: task.id,
        })
      );
    } else {
      dispatch(
        unbindParameterDefinitionOfTask({
          ...paramUpdate,
          fieldName,
          taskId,
        })
      );
    }
  }, [applyParamValue, fieldName, parameter, taskId, dispatch, task]);

  if (!parameter) return;

  const defaultValue = getParameterValueStr(parameter);
  const parameterType = getParameterType(parameter);

  return (
    <Stack
      justifyContent="space-between"
      direction="row"
      alignItems="center"
      sx={{
        backgroundColor: 'neutral.800',
        borderRadius: '4px',
        padding: '4px 8px',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack direction="row">
        {isHovered ? (
          // TODO onClickHandler will be added later.
          <IconButton
            sx={{ padding: 0, color: 'white' }}
            onClick={handleUnbindParameter}
          >
            <CancelOutlinedIcon />
          </IconButton>
        ) : (
          getParameterIcon(parameterType, 'white')
        )}
        <Typography
          marginLeft={1}
          flex={1}
          variant="subtitle2"
          color="white"
          noWrap
          sx={{
            maxWidth: '100px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {parameter.name}
        </Typography>
      </Stack>
      <Typography
        marginLeft={1}
        variant="subtitle2"
        color="white"
        noWrap
        sx={{
          maxWidth: '120px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {defaultValue}
      </Typography>
    </Stack>
  );
};
